import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { formatDateForPeriod, getFormattedData } from "../dateUtils";
import { transformChartData } from "../charthelper";

const BarChart = ({ chartUtils, colors }) => {

  // Peak filtering state
  const [applyPeakFilter, setApplyPeakFilter] = useState(false);
  const [thresholdPercent, setThresholdPercent] = useState(200);

  const combinedData = chartUtils?.series?.flatMap(
    (seriesItem, seriesIndex) => {
      if (!seriesItem || !seriesItem.data) return []; // Ensure seriesItem and seriesItem.data are valid

      const categories = chartUtils?.category?.length
        ? chartUtils?.category
        : seriesItem.data.map((_, index) => `Data ${index + 1}`);
      return categories.map((date, index) => ({
        date,
        value: seriesItem?.data?.[index] || 0,
        name: seriesItem.name,
      }));
    }
  );

  const formattedData = getFormattedData(
    combinedData || [],
    chartUtils?.chart_period || "daily"
  );
  // Calculate chart width based on number of categories and fixed bar width
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const categories = chartUtils?.options?.withoutxdate
    ? chartUtils?.series?.map((x) => x?.name)
    : chartUtils?.categorywise?.length
      ? [...new Set(chartUtils?.categorywise.map((item) => item.category))]
      : formattedData.categories;


  // Calculate minimum chart width (50px per bar plus some padding)
  const minChartWidth = Math.max(categories?.length * 70, 500);

  const getFontSize = () => {
    if (chartUtils?.category?.length <= 20) return "12px";
    if (chartUtils?.category?.length <= 50) return "10px";
    return "8px";
  };


  function rotateDataLabels(chartContext) {
    setTimeout(() => {
      const chart = chartContext.el;
      const allTexts = chart.querySelectorAll('.apexcharts-datalabel text');

      allTexts.forEach((textElement) => {
        const text = textElement.textContent.trim();
        const isNumeric = /^\d+(\.\d+)?$/.test(text);
        const x = parseFloat(textElement.getAttribute('x')) || 0;
        const y = parseFloat(textElement.getAttribute('y')) || 0;

        if (isNumeric && x > 0 && y > 0) {
          textElement.setAttribute('transform', `rotate(-45 ${x} ${y})`);
        }
      });
    }, 500); // Wait until chart animations complete
  }


  const options = {
    chart: {
      zoom: { enabled: true },
      events: {
        mounted: function (chartContext, config) {
          setTimeout(() => {
            const chart = chartContext.el;
            // First, try to find all text elements in the chart
            const allTexts = chart.querySelectorAll('text');

            allTexts.forEach(textElement => {
              const text = textElement.textContent.trim();

              const isNumeric = /^\d+(\.\d+)?$/.test(text);
              const x = parseFloat(textElement.getAttribute('x')) || 0;
              const y = parseFloat(textElement.getAttribute('y')) || 0;

              // Additional checks to ensure it's a data label
              const parent = textElement.parentElement;
              const isDataLabel = parent && (
                parent.classList.contains('apexcharts-datalabel') ||
                parent.classList.contains('apexcharts-datalabels') ||
                parent.parentElement?.classList.contains('apexcharts-datalabels')
              );

              if (isNumeric && isDataLabel && x > 0 && y > 0) {
                // Apply rotation
                textElement.setAttribute('transform', `rotate(-45 ${x} ${y})`);
              }
            });
          }, 1000);
        },
        updated: function (chartContext, config) {
          setTimeout(() => {
            const chart = chartContext.el;
            const allTexts = chart.querySelectorAll('text');

            allTexts.forEach(textElement => {
              const text = textElement.textContent.trim();
              const isNumeric = /^\d+(\.\d+)?$/.test(text);
              const x = parseFloat(textElement.getAttribute('x')) || 0;
              const y = parseFloat(textElement.getAttribute('y')) || 0;

              const parent = textElement.parentElement;
              const isDataLabel = parent && (
                parent.classList.contains('apexcharts-datalabel') ||
                parent.classList.contains('apexcharts-datalabels') ||
                parent.parentElement?.classList.contains('apexcharts-datalabels')
              );

              if (isNumeric && isDataLabel && x > 0 && y > 0) {
                textElement.setAttribute('transform', `rotate(-45 ${x} ${y})`);
              }
            });
          }, 1000);
        },
      },
      type: "bar",
      stacked: chartUtils?.options?.stacked,
      autoScale: false,
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
      toolbar: {
        show: true,
        offsetY: 2,  // Push toolbar slightly down
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      scrollable: true,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "50%",
        maxBarThickness: 20,
        dataLabels: {
          position: "top",
          offsetY: -10,
          style: {
            colors: ["#333"],
          },
        },
      },
    },
    states: {
      hover: {
        filter: {
          type: "none",
        },
      },
    },
    xaxis: {
      type: "datetime",
      labels: {
        rotate: -45,
        datetimeUTC: false,
        text: chartUtils?.axis?.x || "Date",
      },
    },
    fill: {
      colors: colors || [
        chartUtils?.colors?.[chartUtils?.series[0]?.name] || "#008FFB",
      ],
      type: "gradient",
      gradient: {
        shadeIntensity: 0.5,
        opacityFrom: 0.9,
        opacityTo: 0.9,
        stops: [0, 90, 100],
      },
    },
    dataLabels: {
      enabled: chartUtils?.category?.length <= 70,
      formatter: (val) => val.toFixed(2),
      style: {
        colors: colors || [
          chartUtils?.colors?.[chartUtils?.series[0]?.name] || "#008FFB",
        ],
        fontSize: getFontSize(),
      },
      offsetY: -25,
      offsetX: 10,
    },
    grid: {
      show: true,
      borderColor: "#ccc",
      strokeDashArray: 1,
      position: "back",
      padding: {
        top: 40,  // Adds padding below toolbar
        right: 0,
        bottom: 0,
        left: 0
      },
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => value.toFixed(2),
      },
      title: {
        text: chartUtils?.axis?.y || "Value",
      },
      strokeWidth: 2,
      forceNiceScale: true,
      autoScaleYaxis: true,
    },
    tooltip: {
      x: {
        formatter: (value) => value,
      },
    },
  };

  let series = [];
  if (chartUtils?.categorywise?.length) {
    series = Array.isArray(chartUtils?.categorywise)
      ? chartUtils.categorywise.map((item) => ({
        name: item.name,
        data: chartUtils?.categorywise.map((catItem) =>
          catItem.category === item.category &&
            Array.isArray(item?.data) &&
            item.data.length > 0
            ? item.data[0]
            : 0
        ),
      }))
      : formattedData.seriesData || [];
  } else if (chartUtils?.options?.calcMethod === "DEVIATION") {
    series = chartUtils?.series;
  } else if (chartUtils?.options?.withoutxdate) {
    series = [
      {
        name: "Results",
        data: formattedData.seriesData?.map((item) => item.data)?.flat() || [], // Flatten the nested arrays
      },
    ];
  } else {
    series = formattedData.seriesData || [];
  }


  // Peak filtering function
  const filterPeakValuesWithCategories = (series, categories, percentage) => {
    const flatValues = series.flatMap((s) => s.data).filter((v) => typeof v === "number");

    if (flatValues.length === 0) return { series, categories };

    const avg = flatValues.reduce((sum, val) => sum + val, 0) / flatValues.length;
    const threshold = avg * (1 + percentage / 100);


    const indicesToRemove = new Set();

    series.forEach((s) => {
      s.data.forEach((value, index) => {
        if (typeof value === "number" && value > threshold) {
          indicesToRemove.add(index);
        }
      });
    });


    const filteredSeries = series.map((s) => ({
      ...s,
      data: s.data.filter((_, index) => !indicesToRemove.has(index))
    }));

    const filteredCategories = categories.filter((_, index) => !indicesToRemove.has(index));

    return { series: filteredSeries, categories: filteredCategories };
  };

  // Apply filtering if enabled
  const filteredResult = applyPeakFilter
    ? filterPeakValuesWithCategories(series, categories, thresholdPercent)
    : { series, categories };

  const filteredSeries = filteredResult.series;
  const filteredCategories = filteredResult.categories;

  // Update options with filtered categories
  const finalOptions = {
    ...options,
    xaxis: {
      ...options.xaxis,
      categories: filteredCategories
    }
  };


  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div>
        {/* Peak filtering controls */}
        <div className="mb-4 flex gap-4 items-center">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={applyPeakFilter}
              onChange={(e) => setApplyPeakFilter(e.target.checked)}
            />
            Filter Peaks
          </label>
          {applyPeakFilter && (
            <label className="flex items-center gap-2">
              Threshold %
              <input
                type="number"
                value={thresholdPercent}
                min={100}
                max={1000}
                onChange={(e) => setThresholdPercent(Number(e.target.value))}
                className="border px-2 py-1 w-20"
              />
            </label>
          )}
        </div>

        {chartUtils?.chart_type === "BARCHART" ? (
          <Chart
            options={finalOptions}
            series={transformChartData(filteredCategories, filteredSeries)}
            type="bar"
            height={350}
            width="100%"
          />
        ) : (
          ""
        )}
      </div>
    </div>
  );
};

export default BarChart;
